//---------------------------------------------------------
// Imports
//---------------------------------------------------------
import {Divider, Paper, Stack} from '@mui/material';
import {get_filtered_number, get_price_str, get_value} from 'common';
import ValueSetter from 'components/ValueSetter';
import {useMarketInfo, usePrice} from 'hooks/marketHooks';
import {useBalance} from 'hooks/userHooks';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {IValueSet} from 'types';
import OrderButtonBox from './OrderButtonBox';

// Scaling factor to adjust the minimum total cost based on market requirements
const SCALING_FACTOR = 0.001;

//---------------------------------------------------------
// Functional Component
//---------------------------------------------------------
export default function OrderMaker(props: {exchange: string; ticker: string; disabled?: boolean}) {
	const {exchange, ticker, disabled} = props;

	const initial_seed_mod = 0.1;
	const initial_order_price_mod = 1.001;
	const initial_target_profit_mod = 0.04;
	const initial_loss_cut_mod = -0.03;

	const [seed, set_seed] = useState<IValueSet>({modifier: initial_seed_mod, ref_value: 0});
	const [order_price, set_order_price] = useState<IValueSet>({modifier: initial_order_price_mod, ref_value: 0});
	const [target_profit, set_target_profit] = useState<IValueSet>({modifier: initial_target_profit_mod, ref_value: 0});
	const [loss_cut, set_loss_cut] = useState<IValueSet>({modifier: initial_loss_cut_mod, ref_value: 0});

	const {price: current_price} = usePrice(exchange, ticker);
	const {market_info} = useMarketInfo(exchange, ticker);
	const {balance} = useBalance();

	const min_total_cost = useMemo(() => (market_info ? parseFloat(market_info.market.bid.min_total) : 0), [market_info]);
	const scaled_min_total_cost = useMemo(() => min_total_cost * SCALING_FACTOR, [min_total_cost]);
	const bid_fee = useMemo(() => parseFloat(market_info?.bid_fee ?? '0'), [market_info]);
	const ask_fee = useMemo(() => parseFloat(market_info?.ask_fee ?? '0'), [market_info]);
	const seed_value = useMemo(() => get_filtered_number(get_value(seed)), [seed]);
	const fee_value = useMemo(() => seed_value * (bid_fee + ask_fee), [seed_value, bid_fee, ask_fee]);
	const fee_str = useMemo(() => get_price_str(fee_value), [fee_value]);

	useEffect(() => {
		if (balance) {
			const min_mod = scaled_min_total_cost / balance;
			set_seed({
				modifier: Math.max(0.1, min_mod),
				ref_value: balance,
			});
		}
	}, [balance, min_total_cost]);

	useEffect(() => {
		if (current_price) {
			if (order_price)
				set_order_price({
					modifier: order_price.modifier,
					ref_value: current_price,
				});
			else
				set_order_price({
					modifier: initial_order_price_mod,
					ref_value: current_price,
				});
		}
	}, [current_price]);

	useEffect(() => {
		if (market_info) {
			set_target_profit({
				modifier: target_profit.modifier,
				ref_value: seed_value,
				calibrator: -fee_value,
			});

			set_loss_cut({
				modifier: loss_cut.modifier,
				ref_value: seed_value,
				calibrator: -fee_value,
			});
		}
	}, [market_info, seed_value]);

	const handleAI = useCallback(() => {
		if (!current_price || !market_info) return;

		const mod_price = 1 + Math.random() * 0.001;
		const mod_target = Math.random() * 0.001 + 0.0005;
		const mod_loss = -Math.random() * 0.001 - 0.005;

		set_order_price({
			modifier: mod_price,
			ref_value: current_price,
		});

		set_target_profit({...target_profit, modifier: mod_target});
		set_loss_cut({...loss_cut, modifier: mod_loss});
	}, [current_price, market_info]);

	const handleChangeTarget = useCallback(
		(value: IValueSet) => {
			const new_value_set = {...value, calibrator: -fee_value};
			const new_value = get_value(new_value_set);
			if (new_value > 0) set_target_profit(new_value_set);
		},
		[target_profit, fee_value],
	);

	const handleChangeLoss = useCallback(
		(value: IValueSet) => {
			const new_value_set = {...value, calibrator: -fee_value};
			const new_value = get_value(new_value_set);
			const seed_value = get_value(seed);
			if (new_value < seed_value && new_value_set.modifier < target_profit.modifier) set_loss_cut(new_value_set);
		},
		[loss_cut, fee_value, seed, target_profit],
	);

	return (
		<Stack id="order-maker" width="100%" alignItems="center" spacing={2}>
			<Stack width="100%" maxWidth="600px" spacing={1}>
				<ValueSetter name="SD" value={seed} onChange={set_seed} color={seed_value < scaled_min_total_cost ? 'error.main' : 'grey.300'} units={[1, 5]} />

				<Divider />

				<ValueSetter name="BY" value={order_price} onChange={set_order_price} />
				<ValueSetter name="TG" value={target_profit} onChange={handleChangeTarget} color="#00bcd4" bgcolor="#01631aff" negative tooltip={`Inc. fee(${fee_str})`} />
				<ValueSetter name="LS" value={loss_cut} onChange={handleChangeLoss} color="error.main" bgcolor="#6b0000ff" negative tooltip={`Inc. fee(${fee_str})`} />
			</Stack>

			<OrderButtonBox
				disabled={disabled || balance < seed_value || seed_value < min_total_cost}
				bid_fee={bid_fee}
				ask_fee={ask_fee}
				total_cost={seed_value}
				order_price={order_price}
				target_profit={target_profit}
				loss_cut={loss_cut}
				handleAI={handleAI}
			/>
		</Stack>
	);
}
